<script setup lang="ts">
import GaiaSectionHeader from '../base/GaiaSectionHeader.vue';
import { gaiaNeutral, gaiaShellPadding } from '../themes/tokens';

const props = defineProps({
  /** Convenience: renders a GaiaSectionHeader; use #header for a custom one. */
  title: {
    type: String,
    default: undefined,
  },
  /**
   * Fills its mount by default — inside Layout's drawer the sider's
   * `drawer-width` already governs, and a second width here just leaves a
   * dead strip. Pass one only where the panel stands on its own.
   */
  width: {
    type: String,
    default: '100%',
  },
});

const railBorder = gaiaNeutral.borderSubtle;
// Shared with Layout's content column: same top, so this panel's header and the
// page's own first heading sit on the same line.
const padTop = gaiaShellPadding.top;
const padX = gaiaShellPadding.railX;
</script>

<template>
  <!-- The right rail from mock 5a: a column separated by a hairline
       border-left. Purely a mount point — the content (agent activity, a
       chat, …) comes entirely from the slots. -->
  <aside class="gaia-panel" :style="{ width: props.width }">
    <n-flex
      v-if="props.title || $slots.header || $slots['header-extra']"
      align="baseline"
      justify="space-between"
      :size="8"
      :wrap="false"
      :style="{ marginBottom: '14px' }"
    >
      <slot name="header">
        <gaia-section-header>{{ props.title }}</gaia-section-header>
      </slot>
      <span v-if="$slots['header-extra']" class="header-extra">
        <slot name="header-extra" />
      </span>
    </n-flex>
    <div :style="{ flex: 1, minHeight: 0, overflowY: 'auto' }">
      <slot />
    </div>
    <!-- Pinned under the scrolling body — a form's Cancel / Save. -->
    <div v-if="$slots.footer" :style="{ flex: 'none', paddingTop: '14px' }">
      <slot name="footer" />
    </div>
  </aside>
</template>

<style scoped>
/* Fills its mount's height so `.body` below is the thing that scrolls and the
   header stays put; a mount without a definite height just gets `auto`. */
.gaia-panel {
  flex: none;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  height: 100%;
  border-left: 1px solid v-bind(railBorder);
  padding: v-bind(padTop) v-bind(padX);
}
</style>
